import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getMarket, purchaseMarketItem } from '../api/market';
import { fetchAvatarSvg } from '../utils';
import { hotPotatoGraphicUrl } from '../api/hotPotato';
import { HotPotatoIcon } from '../assets/icons';
import '../styles/SpudMarketPage.css';

// Hot Potatoes are gifts sent to friends, not entitlements, so they're not a
// market_items row — the card here just links to the send flow.
const HOT_POTATO_PREVIEW = 'mid_toss.svg';

const TYPE_LABELS = {
    avatar: 'Profile avatar',
    reaction: 'Reaction emoji',
};

// Shown once an item is owned, so buyers know where to go and use it.
const TYPE_USAGE_HINTS = {
    avatar: 'Choose it when you edit your profile.',
    reaction: 'Available in your reaction picker on activities and Hot Potatoes.',
};

const SpudMarketPage = () => {
    const history = useHistory();
    const [items, setItems] = useState([]);
    const [balance, setBalance] = useState(0);
    const [hotPotatoCost, setHotPotatoCost] = useState(null);
    const [avatarPreviews, setAvatarPreviews] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [pendingItem, setPendingItem] = useState(null);
    const [purchasing, setPurchasing] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getMarket();
                if (!mounted) return;
                setItems(result.items || []);
                setBalance(result.spudcoin_balance ?? 0);
                setHotPotatoCost(result.hot_potato_cost ?? null);

                const avatarFiles = (result.items || [])
                    .filter((item) => item.item_type === 'avatar')
                    .map((item) => item.payload);
                const previews = {};
                await Promise.all(
                    avatarFiles.map(async (filename) => {
                        try {
                            previews[filename] = await fetchAvatarSvg(filename);
                        } catch (err) {
                            console.error(`Failed to load avatar preview ${filename}:`, err);
                        }
                    })
                );
                if (mounted) setAvatarPreviews(previews);
            } catch (err) {
                if (mounted) setError(err.message || 'Failed to load SpudMarket');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const handleConfirmPurchase = async () => {
        if (!pendingItem) return;
        setPurchasing(true);
        setError(null);
        setSuccess(null);
        try {
            const result = await purchaseMarketItem(pendingItem.id);
            setBalance(result.spudcoin_balance ?? balance);
            setItems((prev) =>
                prev.map((item) => (item.id === pendingItem.id ? { ...item, owned: true } : item))
            );
            setSuccess(result.message || `${pendingItem.name} unlocked!`);
            setPendingItem(null);
        } catch (err) {
            if (err.spudcoin_balance != null) setBalance(err.spudcoin_balance);
            setError(err.message || 'Failed to complete purchase');
            setPendingItem(null);
        } finally {
            setPurchasing(false);
        }
    };

    const renderPreview = (item) => {
        if (item.item_type === 'avatar') {
            const svg = avatarPreviews[item.payload];
            return svg ? (
                <div className="sm-card-avatar" dangerouslySetInnerHTML={{ __html: svg }} />
            ) : (
                <div className="sm-card-emoji">{item.icon || '🥔'}</div>
            );
        }
        return <div className="sm-card-emoji">{item.payload || item.icon || '🥔'}</div>;
    };

    if (loading) {
        return (
            <div className="sm-page">
                <div className="sm-container">
                    <div className="sc-loading">
                        <div className="sc-spinner" />
                        <p>Loading SpudMarket...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sm-page">
            <div className="sm-container">

                <div className="sm-header">
                    <div className="sm-header-main">
                        <button className="sc-back-link" onClick={() => history.push('/achievements')}>
                            ‹ Achievements
                        </button>
                        <h2>SpudMarket</h2>
                        <p className="sm-header-sub">Spend the SpudCoin you earned from achievements</p>
                    </div>
                    <div className="sm-balance">
                        <span className="sm-balance-icon">🪙</span>
                        <span className="sm-balance-value">{balance}</span>
                        <span className="sm-balance-label">SpudCoin</span>
                    </div>
                </div>

                {error && <div className="sm-alert sc-alert sc-alert--error">⚠ {error}</div>}
                {success && <div className="sm-alert sc-alert sc-alert--success">✓ {success}</div>}

                <div className="sm-content">
                    <div className="sm-grid">
                        <div className="sm-card sm-card--hot">
                            <div className="sm-card-preview">
                                <img
                                    className="sm-card-graphic"
                                    src={hotPotatoGraphicUrl(HOT_POTATO_PREVIEW)}
                                    alt=""
                                />
                            </div>
                            <span className="sm-card-type">Gift</span>
                            <h3 className="sm-card-name">Hot Potato</h3>
                            <p className="sm-card-desc">
                                Send a friend a sizzling message and graphic. Pick from 5 of each.
                            </p>
                            <div className="sm-card-footer">
                                <span className="sm-price">🪙 {hotPotatoCost ?? '—'}</span>
                                <button
                                    className="sc-btn sc-btn--primary sc-btn--sm"
                                    onClick={() => history.push('/hot-potato/send')}
                                >
                                    <HotPotatoIcon aria-hidden="true" />
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>

                    {items.length === 0 ? (
                        <p className="sm-empty">The market is restocking. Check back soon!</p>
                    ) : (
                        <div className="sm-grid sm-grid--items">
                            {items.map((item) => {
                                const affordable = balance >= item.price_spudcoin;
                                return (
                                    <div
                                        key={item.id}
                                        className={`sm-card ${item.owned ? 'owned' : ''}`}
                                    >
                                        {item.owned && <span className="sm-owned-badge">✓ Purchased</span>}
                                        <div className="sm-card-preview">{renderPreview(item)}</div>
                                        <span className="sm-card-type">
                                            {TYPE_LABELS[item.item_type] || item.item_type}
                                        </span>
                                        <h3 className="sm-card-name">{item.name}</h3>
                                        <p className="sm-card-desc">{item.description}</p>
                                        {item.owned && (
                                            <p className="sm-card-hint">{TYPE_USAGE_HINTS[item.item_type]}</p>
                                        )}
                                        <div className="sm-card-footer">
                                            <span className="sm-price">🪙 {item.price_spudcoin}</span>
                                            {item.owned ? (
                                                <span className="sm-owned-label">Owned</span>
                                            ) : (
                                                <button
                                                    className="sc-btn sc-btn--primary sc-btn--sm"
                                                    disabled={!affordable}
                                                    onClick={() => {
                                                        setError(null);
                                                        setSuccess(null);
                                                        setPendingItem(item);
                                                    }}
                                                >
                                                    {affordable ? 'Buy' : 'Not enough SpudCoin'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

            </div>

            {pendingItem && (
                <div className="sc-overlay" onClick={() => !purchasing && setPendingItem(null)}>
                    <div className="sc-modal sm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="sm-modal-title">Confirm purchase</h3>
                        <p className="sm-modal-body">
                            Spend <strong>🪙 {pendingItem.price_spudcoin} SpudCoin</strong> on{' '}
                            <strong>{pendingItem.name}</strong>?
                        </p>
                        <p className="sm-modal-note">
                            You'll have 🪙 {balance - pendingItem.price_spudcoin} left. Purchases are final.
                        </p>
                        <div className="sm-modal-actions">
                            <button
                                className="sc-btn sc-btn--secondary"
                                onClick={() => setPendingItem(null)}
                                disabled={purchasing}
                            >
                                Cancel
                            </button>
                            <button
                                className="sc-btn sc-btn--primary"
                                onClick={handleConfirmPurchase}
                                disabled={purchasing}
                            >
                                {purchasing ? 'Purchasing...' : 'Yes, spend SpudCoin'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpudMarketPage;
